// Level Loader Service
// Handles loading level JSON files and caching

import { LevelData, validateLevelData } from './LevelData';

class LevelLoaderService {
  private cache: Map<string, LevelData> = new Map();
  private basePath: string = '/levels';

  // Load a level by ID (e.g., 'level-1-1')
  async load(levelId: string): Promise<LevelData> {
    // Check cache first
    if (this.cache.has(levelId)) {
      return this.cache.get(levelId)!;
    }

    const url = `${this.basePath}/${levelId}.json`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to load level: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Validate the level data
      if (!validateLevelData(data)) {
        throw new Error(`Invalid level data format for ${levelId}`);
      }

      // Cache it
      this.cache.set(levelId, data);

      return data;
    } catch (error) {
      console.error(`Error loading level ${levelId}:`, error);
      throw error;
    }
  }

  // Load a level by zone and number (e.g., zone 1, level 1)
  async loadByZone(zone: number, levelNumber: number): Promise<LevelData> {
    const levelId = `level-${zone}-${levelNumber}`;
    return this.load(levelId);
  }

  // Preload multiple levels
  async preloadLevels(levelIds: string[]): Promise<void> {
    await Promise.all(levelIds.map(id => this.load(id)));
  }

  // Clear cache (useful for level editor hot-reloading)
  clearCache(): void {
    this.cache.clear();
  }

  // Get cached level without loading
  getCached(levelId: string): LevelData | undefined {
    return this.cache.get(levelId);
  }

  // Set the base path for level files
  setBasePath(path: string): void {
    this.basePath = path;
  }
}

// Export singleton instance
export const LevelLoader = new LevelLoaderService();

// Also export the class for testing
export { LevelLoaderService };
